/**
 * One row of the drawer's Issues list, laid out as a compact grid with three
 * columns: [severity icon 20px | content 1fr | controls].
 *   - Content zone (col 2): title + business-type icon (row 1), then a meta
 *     stack (row 2) - the status-detail line over the quiet detected-age line.
 *   - Control zone (col 3): kebab + chevron, top-right of the title row.
 * Title, meta and the expanded body all align to the content column; the
 * severity icon never indents them. The Data Discrepancy / Process Issue type
 * rides the header as an icon (tooltip label) beside the name.
 *
 * ONE status signal: the left rail + severity icon source the shared STATUS_FG
 * (issuesState); the lifecycle WORD rides the icon's hover tooltip - there is
 * no right-side pill. Closed rows (resolved/ignored) recede: no rail, a muted
 * icon, a muted title. Below the detected-age line the row stacks its quiet
 * annotations in order: a borderless "Marked fixed · {state}" progress
 * indicator (teal assignment-tick while awaiting the next sync, red
 * assignment-warning once a later sync still saw it - never "Fixed"; green/check
 * = sync-confirmed resolved only), then a "reason" note card for a blocked /
 * ignored row - a soft grey surface holding a quiet label, the reason (clamped
 * to two lines, full on hover) and a muted "{actor} · {ago}" byline, so the
 * human note has a clear boundary. Split out of IssuesEntryList.tsx.
 */
import React, { useEffect, useRef, useState } from 'react'
import { Box, Tooltip, Typography } from '@mui/material'
import BlockIcon from '@mui/icons-material/Block'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import AssignmentTurnedInOutlinedIcon from '@mui/icons-material/AssignmentTurnedInOutlined'
import AssignmentLateOutlinedIcon from '@mui/icons-material/AssignmentLateOutlined'
import { ROW_HOVER_SOFT } from '../../../theme/exceptionTableStyles'
import { poolwerxColors } from '../../../theme/colors'
import { EXCEPTION_TYPE_META } from '../../../components/SystemIcons'
import { formatDate, formatDetectedAge } from '../../../utils/inspectorFormatters'
import {
  DISABLED_FG,
  FIX_FAILED_FG,
  IGNORED_FG,
  MARKED_FIXED_FG,
  STATUS_FG,
  STATUS_LABEL,
  entryDateMeta,
  fixMarker,
  fixMarkerTooltipNode,
  resolveEntryStatus,
  statusDetail,
  type FixMarker,
  type IssuesChipEntry,
} from './issuesState'
import type { RowAction } from './transitions'
import type { ExceptionDrawerDetailBlock } from '../types'
import IssuesRowKebab from './IssuesRowKebab'
import { OpenedFromDetail, SiblingDetail } from './IssuesEntryDetail'

// Muted slate-teal for the resolved row's text-only detail ("Fixed by X" /
// "Resolved by sync") - quieter than the lifecycle copy, distinct from the
// blocked/ignored reason notes (which read in text.secondary).
const RESOLVED_DETAIL_FG = '#5F8A94'

// Muted "{actor} · {ago}" byline for a reason note - who left the note and when,
// so the blocked/ignored reason reads as a human comment. Drops unknown parts;
// null when neither is known. Uses the detected-age relative form so the "{ago}"
// reads consistently with the row's detected line.
const reasonByline = (name?: string | null, iso?: string | null): string | null => {
  const who = name?.trim()
  const ago = iso ? formatDetectedAge(iso) : null
  return [who, ago].filter(Boolean).join(' · ') || null
}

export interface IssuesEntryRowProps {
  entry: IssuesChipEntry
  index: number
  dense: boolean
  fpCode?: string
  showRowActions?: boolean
  onRowAction?: (entry: IssuesChipEntry, action: RowAction) => void
  openedFromCode?: string
  // The drawer's interactive Issues list: every tag-backed row expands to its
  // Why/How and drops the read-only "(current)" tag - independent of whether a
  // focus (``openedFromCode``) is set, so a no-focus drawer (Data Explorer)
  // still renders expandable rows. The read-only chip/tooltip mode leaves this
  // off. (``openedFromCode`` set implies interactive too, for back-compat.)
  interactive?: boolean
  actionsPending?: boolean
  eagerDetailCode?: string
  eagerDetailBlocks?: ExceptionDrawerDetailBlock[]
}

const iconForEntry = (entry: IssuesChipEntry, size: number): React.ReactNode => {
  if (entry.blocked) return <BlockIcon sx={{ fontSize: size }} />
  if (entry.kind === 'ignored') return <VisibilityOffIcon sx={{ fontSize: size }} />
  if (entry.kind === 'resolved') {
    return <CheckCircleOutlineIcon sx={{ fontSize: size }} />
  }
  // Marked fixed but still open keeps the amber warning glyph - it's awaiting
  // sync confirmation, so it reads as "still to process", not done.
  return <WarningAmberIcon sx={{ fontSize: size }} />
}

// Business-classification (Data Discrepancy / Process Issue) glyph that rides the
// header beside the title - icon-only in the type's own tint, with the label on
// hover. Renders nothing when the entry has no (or an unknown) classification.
const TypeIcon: React.FC<{ classification?: string | null }> = ({
  classification,
}) => {
  const meta = classification ? EXCEPTION_TYPE_META[classification] : undefined
  if (!meta?.icon) return null
  const Icon = meta.icon
  return (
    <Tooltip title={meta.label} arrow placement="top" enterDelay={300}>
      <Box
        component="span"
        aria-label={meta.label}
        sx={{
          flexShrink: 0,
          display: 'inline-flex',
          alignItems: 'center',
          color: meta.color,
          cursor: 'default',
        }}
      >
        <Icon sx={{ fontSize: 15 }} />
      </Box>
    </Tooltip>
  )
}

// Borderless "Marked fixed · {state}" progress indicator for an OPEN row (the
// blocked/destructive RED on the title-row ban icon means blocked, not this).
// Two states by colour + icon + suffix: teal assignment-tick while awaiting the
// next sync, red assignment-warning once a later sync still flagged it. The
// who/when hover detail rides ``fixMarker``'s tooltip.
const MarkedFixedIndicator: React.FC<{ marker: FixMarker }> = ({ marker }) => {
  const awaiting = marker.state === 'awaiting_sync'
  const color = awaiting ? MARKED_FIXED_FG : FIX_FAILED_FG
  const Icon = awaiting ? AssignmentTurnedInOutlinedIcon : AssignmentLateOutlinedIcon
  const suffix = awaiting ? 'awaiting next sync' : 'check failed'
  const node = (
    <Box
      component="span"
      sx={{
        mt: '12px',
        alignSelf: 'flex-start',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '5px',
        color,
        fontSize: '12px',
        lineHeight: 1.3,
        cursor: 'default',
      }}
    >
      <Icon sx={{ fontSize: 14, flexShrink: 0 }} />
      <Box component="span">
        <Box component="span" sx={{ fontWeight: 500 }}>
          {marker.label}
        </Box>
        <Box component="span" sx={{ fontWeight: 400 }}>{` · ${suffix}`}</Box>
      </Box>
    </Box>
  )
  return marker.tooltip.length > 0 ? (
    <Tooltip
      title={fixMarkerTooltipNode(marker.tooltip)}
      arrow
      placement="top"
      enterDelay={250}
    >
      {node}
    </Tooltip>
  ) : (
    node
  )
}

// "Note card" for a blocked / ignored reason - a soft grey surface + hairline so
// the human note has a clear boundary and doesn't get lost in the column (no
// second status icon - the lifecycle status already shows on the title-row
// icon). Quiet "Blocked reason" label, the reason itself (clamped to two lines,
// full on hover) and a muted "{actor} · {ago}" byline.
const ReasonNote: React.FC<{
  lead: string
  reason: string
  byline: string | null
}> = ({ lead, reason, byline }) => (
  <Tooltip title={reason} arrow placement="top" enterDelay={400}>
    <Box
      sx={{
        mt: '10px',
        minWidth: 0,
        px: '10px',
        py: '8px',
        borderRadius: '6px',
        bgcolor: poolwerxColors.surface.secondary,
        border: `0.5px solid ${poolwerxColors.border.tertiary}`,
        cursor: 'default',
      }}
    >
      <Box
        sx={{
          fontSize: '11.5px',
          lineHeight: 1.3,
          fontWeight: 600,
          color: poolwerxColors.text.secondary,
        }}
      >
        {lead}
      </Box>
      <Box
        sx={{
          mt: '3px',
          fontSize: '12px',
          lineHeight: 1.35,
          // Slightly darker than the label so the reason itself reads as the
          // content; the label + byline stay quiet metadata.
          color: poolwerxColors.text.primary,
          wordBreak: 'break-word',
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
        }}
      >
        {reason}
      </Box>
      {byline && (
        <Box
          sx={{
            mt: '4px',
            fontSize: '11px',
            lineHeight: 1.3,
            color: poolwerxColors.text.tertiary,
          }}
        >
          {byline}
        </Box>
      )}
    </Box>
  </Tooltip>
)

// Expanded breakdown of the outlets a per-outlet problem fired at (shown above
// the Why/How when a grouped row is opened). Aligns under the title via the same
// body indent the detail blocks use.
const OutletBreakdown: React.FC<{
  names: string[]
  indent: number
  padRight: number
}> = ({ names, indent, padRight }) => (
  <Box sx={{ pl: `${indent}px`, pr: `${padRight}px`, pt: '12px' }}>
    <Box
      sx={{
        fontSize: '11.5px',
        fontWeight: 600,
        color: poolwerxColors.text.secondary,
        mb: '5px',
      }}
    >
      {`Affected outlets (${names.length})`}
    </Box>
    <Box
      component="ul"
      sx={{ m: 0, pl: '18px', display: 'flex', flexDirection: 'column', gap: '3px' }}
    >
      {names.map((name, i) => (
        <Box
          component="li"
          key={`${name}-${i}`}
          sx={{
            fontSize: '12px',
            lineHeight: 1.35,
            color: poolwerxColors.text.primary,
            wordBreak: 'break-word',
          }}
        >
          {name}
        </Box>
      ))}
    </Box>
  </Box>
)

const IssuesEntryRow: React.FC<IssuesEntryRowProps> = ({
  entry,
  index,
  dense,
  fpCode,
  showRowActions = false,
  onRowAction,
  openedFromCode,
  interactive = false,
  actionsPending = false,
  eagerDetailCode,
  eagerDetailBlocks,
}) => {
  const [expanded, setExpanded] = useState(false)
  const rowRef = useRef<HTMLDivElement | null>(null)

  const nameSize = dense ? '13px' : '14px'
  const severitySize = dense ? 16 : 18
  // Severity-icon grid column + the column gap to the title; this sum is the
  // left indent that aligns the expanded body under the TITLE text.
  const severityCol = dense ? 18 : 20
  const colGap = dense ? 6 : 8
  const padXNum = dense ? 10 : 20
  const padX = `${padXNum}px`
  const bodyIndent = padXNum + severityCol + colGap

  const rowState = resolveEntryStatus(entry)
  const stateColor = STATUS_FG[rowState]
  const icon = iconForEntry(entry, severitySize)
  const isCurrent = !!entry.primary
  // The drawer's interactive Issues list - either flagged explicitly
  // (``interactive``) or implied by a focus (``openedFromCode``, kept for
  // back-compat). Every tag-backed row expands; the read-only chip/tooltip mode
  // leaves both off. ``isOpenedFrom`` (the auto-expand target) still needs an
  // actual focus code, so a no-focus interactive list expands nothing on mount.
  const isMultiList = interactive || openedFromCode != null
  const isOpenedFrom = openedFromCode != null && entry.code === openedFromCode
  // In the opened-from list every tag-backed row expands to its own Why/How;
  // in the read-only chip/tooltip list only non-current siblings expand.
  const expandable = isMultiList
    ? entry.tag_id != null && !!fpCode
    : !isCurrent && entry.tag_id != null && !!fpCode

  // Right-edge gutter: the kebab rides the content column (its right edge IS
  // the content's right edge); only the chevron (28) sits beyond, in col 3.
  // Reserve just the chevron + columnGap so the title, meta and the expanded
  // body all wrap to ONE right boundary - the kebab's right edge, before the
  // chevron. Matches the grid's col-3 width + columnGap so the sibling body
  // lines up with the in-grid title/meta.
  const hasKebab = showRowActions && entry.tag_id != null && Boolean(onRowAction)
  const CHEVRON_W = 28
  const bodyPadRight = expandable ? padXNum + colGap + CHEVRON_W : padXNum

  // Opened-from list: auto-expand + scroll the opened-from row into view on
  // mount so the user lands on the issue they clicked, alongside its siblings.
  useEffect(() => {
    if (isOpenedFrom && expandable) {
      setExpanded(true)
      // Optional-call: jsdom (tests) doesn't implement scrollIntoView.
      rowRef.current?.scrollIntoView?.({ block: 'nearest' })
    }
    // Mount-only: the opened-from code doesn't change within a drawer session.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ONE status signal: the left rail + severity icon carry status (the word
  // rides the icon's hover tooltip) - no right-side pill. Closed states recede
  // (muted title, no left rail), but the severity icon keeps its own status
  // colour: the resolved tick stays teal (a sync-confirmed win), ignored grey.
  const closed = rowState === 'resolved' || rowState === 'ignored'
  const iconColor = stateColor
  const statusWord = STATUS_LABEL[rowState]

  // Status annotations stacked under the detected-age line, in order:
  // marked-fixed progress indicator -> blocked reason note -> ignored reason
  // note -> resolved text. The two glanceable one-liners (detected + marked
  // fixed) cluster on top; the heavier reason note sits below, next to the
  // expanded "why flagged" body. Needs-review shows nothing (the date carries
  // the row); resolved renders as plain text ("Fixed by X").
  const marker = fixMarker(entry)
  const blockReason = entry.blocked ? entry.block_reason?.trim() : undefined
  const ignoreReason = entry.kind === 'ignored' ? entry.ignore_reason?.trim() : undefined
  const sd = statusDetail(entry)
  const resolvedText = sd.kind === 'resolved'
  const showDetail = !!marker || !!blockReason || !!ignoreReason || resolvedText
  // Detected date - always shown, directly under the title (first meta line).
  const dateMeta = entryDateMeta(entry)
  // Muted "{actor} · {ago}" byline under each reason note (who left the note +
  // when), so the blocked/ignored reason reads as a human comment.
  const blockByline = blockReason ? reasonByline(entry.blocked_by, entry.blocked_at) : null
  const ignoreByline = ignoreReason ? reasonByline(entry.ignored_by, entry.ignored_at) : null

  const active = expanded

  // Resolved / text-only detail line (no note rule).
  const resolvedContent = (
    <Box
      component="span"
      sx={{
        mt: '6px',
        display: 'inline-flex',
        alignSelf: 'flex-start',
        minWidth: 0,
        maxWidth: '100%',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        fontSize: '12px',
        lineHeight: 1.2,
        fontWeight: 500,
        color: RESOLVED_DETAIL_FG,
        cursor: 'default',
      }}
    >
      {sd.text}
    </Box>
  )

  return (
    <Box
      ref={rowRef}
      sx={{
        borderTop:
          index > 0 ? `0.5px solid ${poolwerxColors.border.tertiary}` : 'none',
        // Slim status rail on open rows so the list scans by colour; closed
        // rows drop the LEFT colour but keep a transparent 3px border so every
        // row stays aligned. Ignored rows instead carry a grey rail on the
        // RIGHT - present like the open rows' rail, but mirrored so it reads as
        // a parked/closed marker rather than active status.
        borderLeft: `3px solid ${closed ? 'transparent' : stateColor}`,
        borderRight: rowState === 'ignored' ? `3px solid ${IGNORED_FG}` : undefined,
      }}
    >
      <Box
        role={expandable ? 'button' : undefined}
        aria-expanded={expandable ? expanded : undefined}
        onClick={expandable ? () => setExpanded((v) => !v) : undefined}
        sx={{
          display: 'grid',
          gridTemplateColumns: `${severityCol}px minmax(0, 1fr) auto`,
          columnGap: `${colGap}px`,
          // Tight title -> detected gap. The kebab/chevron are height-neutralised
          // (negative margins below) so the taller controls don't inflate the
          // title row and push the detected line down.
          rowGap: '2px',
          alignItems: 'center',
          padding: active ? `11px ${padX} 0` : `11px ${padX}`,
          cursor: expandable ? 'pointer' : 'default',
          transition: 'background-color 120ms ease',
          // The kebab stays a steady target (always visible); the chevron is
          // quiet until the row is hovered/focused, then asserts.
          '& .issue-chevron': { opacity: 0.75, transition: 'opacity 120ms ease' },
          '&:hover .issue-chevron, &:focus-within .issue-chevron': { opacity: 1 },
          // No fill on the focused row - it stays white (paper). Collapsed
          // rows get only a soft hover.
          ...(active ? {} : { '&:hover': { bgcolor: ROW_HOVER_SOFT } }),
        }}
      >
        {/* col 1 / row 1: severity icon - colour = status; the status word now
          * rides its hover tooltip (the pill is gone). Muted for closed rows. */}
        <Tooltip title={statusWord} arrow placement="top" enterDelay={300}>
          <Box
            component="span"
            aria-label={statusWord}
            sx={{
              gridColumn: 1,
              gridRow: 1,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: severityCol,
              height: 20,
              color: iconColor,
              cursor: 'default',
            }}
          >
            {icon}
          </Box>
        </Tooltip>
        {/* col 2 / row 1: title + business-type icon, then the kebab at the
          * right of the CONTENT column. The kebab's right edge IS the content
          * column's right edge - so the title, meta and expanded body all end
          * here, with only the chevron beyond (col 3). Title mutes for closed
          * rows. */}
        <Box
          sx={{
            gridColumn: 2,
            gridRow: 1,
            minWidth: 0,
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
          }}
        >
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            <Typography
              component="div"
              sx={{
                minWidth: 0,
                fontSize: nameSize,
                fontWeight: 700,
                color: closed
                  ? poolwerxColors.text.secondary
                  : poolwerxColors.text.primary,
                lineHeight: '20px',
                // Wrap up to two lines, then ellipsize.
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                wordBreak: 'break-word',
              }}
              title={entry.label}
            >
              {entry.label}
              {/* Read-only chip/tooltip list keeps the quiet "(current)" tag. */}
              {isCurrent && !isMultiList && (
                <Box
                  component="span"
                  sx={{
                    ml: 0.75,
                    fontSize: '0.65rem',
                    color: DISABLED_FG,
                    fontWeight: 400,
                  }}
                >
                  (current)
                </Box>
              )}
            </Typography>
            <TypeIcon classification={entry.classification} />
            {/* Per-outlet rollup: the same problem fired at N outlets, folded
              * into this one row. A quiet count pill; the named outlets sit in
              * the expanded breakdown below. */}
            {(entry.outlet_count ?? 0) > 1 && (
              <Box
                component="span"
                aria-label={`${entry.outlet_count} outlets affected`}
                sx={{
                  flexShrink: 0,
                  px: '6px',
                  height: 16,
                  display: 'inline-flex',
                  alignItems: 'center',
                  borderRadius: '8px',
                  fontSize: '10.5px',
                  fontWeight: 700,
                  lineHeight: 1,
                  whiteSpace: 'nowrap',
                  color: poolwerxColors.text.secondary,
                  bgcolor: poolwerxColors.surface.secondary,
                  border: `0.5px solid ${poolwerxColors.border.tertiary}`,
                }}
              >
                {`${entry.outlet_count} outlets`}
              </Box>
            )}
          </Box>
          {hasKebab && (
            <Box
              className="issue-kebab"
              onClick={(e) => e.stopPropagation()}
              // Negative vertical margin: the 32px button stays a full target but
              // doesn't inflate the ~20px title row (keeps the detected line snug).
              sx={{ flexShrink: 0, display: 'flex', alignItems: 'center', my: '-6px' }}
            >
              <IssuesRowKebab
                entry={entry}
                rowState={rowState}
                disabled={actionsPending}
                onAction={onRowAction!}
              />
            </Box>
          )}
        </Box>
        {/* col 3 / row 1: the chevron alone, in the reserved right gutter - the
          * one element that sits beyond the content's right edge. */}
        {expandable && (
          <Box
            className="issue-chevron"
            component="span"
            aria-hidden
            sx={{
              gridColumn: 3,
              gridRow: 1,
              justifySelf: 'end',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 28,
              height: 28,
              // Height-neutralised like the kebab so it doesn't inflate the title row.
              my: '-4px',
              color: poolwerxColors.text.secondary,
            }}
          >
            <ExpandMoreIcon
              sx={{
                fontSize: 20,
                transition: 'transform 120ms ease',
                transform: expanded ? 'rotate(180deg)' : 'none',
              }}
            />
          </Box>
        )}

        {/* col 2 / row 2: meta stack - the detected-age line FIRST (snug under
          * the title), then the marked-fixed indicator, the blocked / ignored
          * reason note, and the resolved text (each owns its own top margin),
          * then the expanded "why flagged" body below. Stays in col 2 so it
          * wraps at the controls' LEFT edge; the sibling body reserves the same
          * gutter (bodyPadRight) for one clean right margin. */}
        {(showDetail || dateMeta) && (
          <Box
            sx={{
              gridColumn: 2,
              gridRow: 2,
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {dateMeta && (
              <Tooltip
                title={`${dateMeta.prefix} ${formatDate(dateMeta.iso, 'datetime')}`}
                arrow
                placement="top"
                enterDelay={300}
              >
                <Box
                  component="span"
                  sx={{
                    mt: '5px',
                    alignSelf: 'flex-start',
                    fontSize: '11.5px',
                    fontWeight: 500,
                    color: poolwerxColors.text.tertiary,
                    whiteSpace: 'nowrap',
                    cursor: 'default',
                  }}
                >
                  {`${dateMeta.prefix} ${formatDetectedAge(dateMeta.iso)}`}
                </Box>
              </Tooltip>
            )}
            {marker && <MarkedFixedIndicator marker={marker} />}
            {blockReason && (
              <ReasonNote lead="Blocked reason" reason={blockReason} byline={blockByline} />
            )}
            {ignoreReason && (
              <ReasonNote lead="Ignored reason" reason={ignoreReason} byline={ignoreByline} />
            )}
            {resolvedText && resolvedContent}
          </Box>
        )}
      </Box>
      {expandable && expanded && (
        <>
          {/* Per-outlet rollup: name the outlets before the shared Why/How.
              When the count says multiple outlets but the names are missing
              (deleted / DNU outlet dropped from the join), fall back to a
              neutral count line so the row isn't a bare "N outlets" badge with
              no breakdown. */}
          {entry.outlet_names && entry.outlet_names.length > 1 ? (
            <OutletBreakdown
              names={entry.outlet_names}
              indent={bodyIndent}
              padRight={bodyPadRight}
            />
          ) : (entry.outlet_count ?? 0) > 1 ? (
            <Box sx={{ pl: `${bodyIndent}px`, pr: `${bodyPadRight}px`, pt: '12px' }}>
              <Box
                sx={{
                  fontSize: '11.5px',
                  fontWeight: 600,
                  color: poolwerxColors.text.secondary,
                }}
              >
                {`Affected at ${entry.outlet_count} outlets`}
              </Box>
            </Box>
          ) : null}
          {eagerDetailBlocks &&
          eagerDetailBlocks.length > 0 &&
          entry.code === eagerDetailCode ? (
            // Focused row: render the eager envelope detail_blocks (no fetch).
            <OpenedFromDetail
              blocks={eagerDetailBlocks}
              links={entry.links}
              indent={bodyIndent}
              padRight={bodyPadRight}
            />
          ) : (
            <SiblingDetail
              fpCode={fpCode as string}
              tagId={entry.tag_id as number}
              indent={bodyIndent}
              padRight={bodyPadRight}
            />
          )}
        </>
      )}
    </Box>
  )
}

export default IssuesEntryRow
